import { GridCellParams } from '@mui/x-data-grid-pro';
import { Box, CircularProgress, Grid, Link } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, { useState } from 'react';
import { colors } from 'styles/styleVariables';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import {
  PlacementDocumentStatus,
  PlacementDocumentType,
  PlacementDocumentSource,
} from 'store/redux-store/placement-documents/types';

import { useDecision } from '@optimizely/react-sdk';
import {
  ff_placement_ui_disable_arbitration_link,
  ff_placement_ui_document_test_ids,
} from 'app/constants/FeatureFlags/Placement/Keys';
import { SharedService } from 'app/services/SharedServices/shared-service';
import { base64DocumentToURL } from 'app/helpers/fileHelper';
import {
  manuallyDecrementPromiseCounter,
  manuallyIncrementPromiseCounter,
  usePromiseTracker,
} from 'react-promise-tracker';
import { useDispatch, useSelector } from 'react-redux';
import { httpSuccess } from 'app/services/serviceHelpers';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { useTranslation } from 'react-i18next';
import { ExceptionType } from 'app/enums/Common';
import { trackException } from 'app-insights/appInsightsTracking';
import { selectUser } from 'oidc/user.selectors';
import { Authorized } from 'oidc/userHelper';
import { userRoles } from 'oidc/userRoles';
import { CustomTooltip } from '@AMIEWEB/Common';
import { useParams } from 'react-router-dom';
import { selectPlacementHeaderData } from 'store/redux-store/placement-details/selectors';
import { isValidPlacementContractDocumentImageGuid } from 'store/redux-store/placement-candidate-contract-doc-editor/types';

const useStyles = makeStyles<{ documentExists: boolean }>()((theme, { documentExists }) => ({
  root: {
    display: 'flex',
    justifyContent: 'flex-start',
    width: '100%',
  },
  downloadIcon: {
    color: documentExists ? colors.amnBlue : 'rgba(142, 142, 142, 1.0)',
  },
  text: {
    paddingLeft: '6px',
    color: documentExists ? colors.amnBlue : colors.text,
    '&:hover': {
      textDecoration: documentExists ? 'underline' : undefined,
    },
  },
  box: {
    width: 'fit-content',
    cursor: documentExists ? 'pointer' : 'default',
  },
  progressBox: {
    display: 'flex',
    'span: first-child': {
      margin: '0 4px',
    },
  },
}));

/**
 * Cell renderer to display an icon, either an imported icon, or a react component
 * @param params X-Grid Params: `GridCellParams`
 * @returns JSXElement
 */
export const DocumentIconCell = (params: GridCellParams, setCredModalProps) => {
  return <DocumentIconCellContent params={params} setCredModalProps={setCredModalProps} />;
};

const DocumentIconCellContent = ({ params, setCredModalProps }: { params: GridCellParams; setCredModalProps }) => {
  const [documentExists, setDocumentExists] = useState<boolean>(false);

  const [fakePlacementDocuments] = useDecision(ff_placement_ui_document_test_ids);
  const [disableArbitrationLink] = useDecision(ff_placement_ui_disable_arbitration_link);
  const { userInfo } = useSelector(selectUser);
  const { t } = useTranslation();
  const { classes, cx } = useStyles({ documentExists });
    const {
      candidate: { id: candidateId },
    } = useSelector(selectPlacementHeaderData);

  const dispatch = useDispatch();

  const isArbitrationAgreemeentDocumentType =
    params.row.documentType && params.row.documentType?.toLowerCase() === PlacementDocumentType?.arbitrationAgreement;
  const isWageRequestForm =
    params.row.documentType && params.row.document?.toLowerCase() === PlacementDocumentType?.wageRequestForm?.toLowerCase();

  /** `documentType: "Contract"` or grid label Candidate Contract — same download rules (any source). */
  const isContractDocumentRow =
    String(params.row.documentType ?? '').toLowerCase() === 'contract' ||
    params.row.document?.toLowerCase() === PlacementDocumentType.contract.toLowerCase();

  /** AMIE Web contract row (by `documentType` or document name). */
  const isWebContractDocumentType =
    params.row.source === PlacementDocumentSource.web && isContractDocumentRow;

  const contractPdfByAmieContractStatus = React.useMemo(() => {
    const s = String(params.row.status ?? '').toLowerCase();
    return s === 'ok to send' || s === 'sent by tcc';
  }, [params.row.status]);

  /** Routing: classic Contract PDF path vs legacy `documentImageId` (when status already allows download). */
  const useClassicContractViewerForWebContract =
    isWebContractDocumentType &&
    params.row.documentId != null &&
    params.row.documentId !== '' &&
    Number(params.row.documentId) !== 0 &&
    (contractPdfByAmieContractStatus || !isValidPlacementContractDocumentImageGuid(params.row.documentImageId));

  const isAmieWebContractPdfEligible =
    isWebContractDocumentType &&
    contractPdfByAmieContractStatus &&
    (useClassicContractViewerForWebContract ||
      (params.row.documentImageId != null &&
        params.row.documentImageId !== '' &&
        String(params.row.documentImageId) !== '0'));

  const { documentId, link, trackerArea } = React.useMemo(() => {
    const isAmieWebContractLegacyDownload =
      params.row.source === PlacementDocumentSource.web &&
      isContractDocumentRow &&
      !useClassicContractViewerForWebContract &&
      !!params.row.documentImageId &&
      params.row.documentImageId !== 0;

    const docId =
      params.row.document?.toLowerCase() === PlacementDocumentType.candidatePacket.toLowerCase() ||
        params.row.document?.toLowerCase() === PlacementDocumentType.clientConfirmation.toLowerCase() ||
        params.row.document?.toLowerCase() === PlacementDocumentType.credentialingPacket.toLowerCase() ||
        params.row.document?.toLowerCase() === PlacementDocumentType.arbitrationAgreement.toLowerCase() ||
        params.row.document?.toLowerCase() === PlacementDocumentType.wageRequestForm.toLowerCase() ||
        isAmieWebContractLegacyDownload
        ? params.row.documentImageId
        : useClassicContractViewerForWebContract
          ? params.row.documentId
          : params.row.documentId ?? 0;

    return {
      documentId: docId,
      link: isWageRequestForm
        ? `/placement/document/${params.row.documentType}/${docId}?candidateId=${candidateId}`
        : useClassicContractViewerForWebContract
          ? `/placement/document/Contract/${docId}`
          : isArbitrationAgreemeentDocumentType || isAmieWebContractLegacyDownload
            ? `/placement/document/${docId}`
            : params.row.documentType
              ? `/placement/document/${params.row.documentType}/${docId}`
              : `/placement/document/${docId}`,
      trackerArea: `#download_document_${docId}`,
    };
  }, [params, candidateId, useClassicContractViewerForWebContract, isContractDocumentRow]);

  const { promiseInProgress } = usePromiseTracker({ area: trackerArea, delay: 0 });

  const isDownloadEnabled = (type: PlacementDocumentType) => {
    switch (type) {
      case PlacementDocumentType.credentialingPacket:
        return Authorized(
          [
            userRoles.credentialing_Leadership,
            userRoles.credentialing_TeamMember,
            userRoles.riskManagement,
            userRoles.complianceAuditSpecialist,
          ],
          userInfo,
        );
      default:
        return true;
    }
  };
  
  const isDocumentValid = () => {
    return (
      /** client confirmation in Sent status */
      (params.row.document?.toLowerCase() === PlacementDocumentType.clientConfirmation.toLowerCase() &&
        params.row.status?.toLowerCase() === PlacementDocumentStatus.sent.toLowerCase()) ||
      /**
       * AMIE Web non-contract rows only (contracts use `isAmieWebContractPdfEligible` + status).
       */
      (params.row.source?.toLowerCase() === PlacementDocumentSource.web.toLowerCase() &&
        !isWebContractDocumentType) ||
      /** Classic candidate contract: only OK to Send / Sent by TCC (same as Web Contract). */
      (isContractDocumentRow &&
        params.row.source?.toLowerCase() === PlacementDocumentSource.classic.toLowerCase() &&
        contractPdfByAmieContractStatus) ||
      /** AMIE Web + Contract: OK to Send / Sent by TCC, then legacy vs classic PDF path */
      isAmieWebContractPdfEligible ||
      /** For Candidate packet */
      (params.row.document?.toLowerCase() === PlacementDocumentType.candidatePacket.toLowerCase() &&
        /** for classic docs in sent status */
        ((params.row.source?.toLowerCase() === PlacementDocumentSource.classic.toLowerCase() &&
          params.row.status?.toLowerCase() === PlacementDocumentStatus.sent.toLowerCase()) ||
          /** source is Amie.Web or web */
          params.row.source?.toLowerCase() === PlacementDocumentSource.web.toLowerCase())) ||
      /** For credentialingPacket */
      (params.row.document?.toLowerCase() === PlacementDocumentType.credentialingPacket.toLowerCase() &&
        isDownloadEnabled(PlacementDocumentType.credentialingPacket)) ||
      /** For Arbitration Agreement */
      (params.row.document?.toLowerCase() === PlacementDocumentType.arbitrationAgreement.toLowerCase() &&
        params.row.status?.toLowerCase() === PlacementDocumentStatus.signed.toLowerCase() &&
        disableArbitrationLink.enabled) ||
      /** For Wage Request Form */
      (params.row.document?.toLowerCase() === PlacementDocumentType.wageRequestForm.toLowerCase() &&
        params.row.status?.toLowerCase() === PlacementDocumentStatus.signed.toLowerCase())
    )
  }

  React.useEffect(() => {
    const validDoc = isDocumentValid();

    setDocumentExists(validDoc && (documentId !== 0 || fakePlacementDocuments.enabled) && !(documentId === '0'));
  }, [documentId, fakePlacementDocuments.enabled, disableArbitrationLink.enabled, params]);

  const downloadDocument = React.useCallback(async () => {
    try {
      manuallyIncrementPromiseCounter(trackerArea);
      const sharedService = SharedService.createInstance();

      const isAmieWebContractLegacyDownload =
        params.row.source === PlacementDocumentSource.web &&
        isContractDocumentRow &&
        !useClassicContractViewerForWebContract &&
        !!params.row.documentImageId &&
        params.row.documentImageId !== 0;

      /** TODO: @SevakSingh @RijashAli Code cleanup - Refactor service for a single endpoint */
      const response = isWageRequestForm
        ? await sharedService.getWageRequestDocument(documentId, candidateId?.toString())
        : isArbitrationAgreemeentDocumentType
          ? await sharedService.getPlacementDocument(documentId)
          : useClassicContractViewerForWebContract
            ? await sharedService.getPlacementClassicDocument('Contract', Number(documentId))
            : isAmieWebContractLegacyDownload
              ? await sharedService.getPlacementDocument(documentId)
              : params.row.documentType
                ? await sharedService.getPlacementClassicDocument(params.row.documentType, Number(documentId))
                : await sharedService.getPlacementDocument(documentId);

      if (response && httpSuccess(response.status)) {
        const payload = response.data.filebytes ?? response.data.data;
        if (payload) {
          base64DocumentToURL(payload, {
            fileType: 'application/pdf',
            download: true,
            fileName: response.data.fileName ?? documentId,
          });
          dispatch(
            globalActions.setSnackBar({
              message: t('placement.profile.tabs.documentTab.successfulDownload'),
              severity: 'success',
            }),
          );
        } else throw new Error('Document is empty!');
      } else
        dispatch(
          globalActions.setSnackBar({
            message: t('placement.profile.tabs.documentTab.failedDownload'),
            severity: 'error',
          }),
        );
      manuallyDecrementPromiseCounter(trackerArea);
    } catch (error) {
      manuallyDecrementPromiseCounter(trackerArea);
      dispatch(
        globalActions.setSnackBar({
          message: t('placement.profile.tabs.documentTab.failedDownload'),
          severity: 'error',
        }),
      );
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.CommonUIError,
          functionName: 'DocumentIconCell_downloadDocument',
          area: 'src/app/components/Placement/PlacementDetails/PlacementTabPanel/DocumentsTab/GridCells/DocumentIconCell.tsx',
        },
      });
    }
  }, [
    documentId,
    params.row,
    candidateId,
    isWageRequestForm,
    isArbitrationAgreemeentDocumentType,
    trackerArea,
    useClassicContractViewerForWebContract,
    isContractDocumentRow,
  ]);

  const handleDownloadClick = React.useCallback(
    event => {
      event.preventDefault();
      event.stopPropagation();
      if (documentExists) {
        /**
        TODO: uncomment this when we have the functionality to mark credentialing packets as sent
        if (
          params.row.document?.toLowerCase() === PlacementDocumentType.credentialingPacket.toLowerCase() &&
          params.row.status?.toLowerCase() === PlacementDocumentStatus.notSent.toLowerCase()
        ) {
          setCredModalProps({ openCred: true, documentId: documentId });
        }
        */
        downloadDocument();
      }
    },
    [documentExists, downloadDocument],
  );

  const tooltipContent = () => {
    return (
      <Grid container direction="column">
        <Grid item>{t('placement.profile.tabs.documentTab.downloadDisabled1')}</Grid>
        <Grid item>{t('placement.profile.tabs.documentTab.downloadDisabled2')}</Grid>
      </Grid>
    );
  };

  const linkContent = React.useMemo(
    () => (
      <Grid
        container
        direction="row"
        alignItems="center"
        className={cx(classes.box, {
          [classes.progressBox]: promiseInProgress,
        })}
      >
        <Grid item>
          {promiseInProgress ? (
            <CircularProgress size={16} />
          ) : (
            <CustomTooltip
              tooltipContent={tooltipContent()}
              disabled={isDownloadEnabled(params.row.document?.toLowerCase())}
              arrow={true}
            >
              <DownloadOutlinedIcon classes={{ root: classes.downloadIcon }} onClick={handleDownloadClick} />
            </CustomTooltip>
          )}
        </Grid>
        <Grid item>
          <span className={classes.text}>{params.row.document}</span>
        </Grid>
      </Grid>
    ),
    [params.row.document, promiseInProgress, handleDownloadClick],
  );

  return (
    <>
      <Box className={classes.root}>
        {documentExists && !promiseInProgress ? (
          <Link href={link} target="_blank">
            <>{linkContent}</>
          </Link>
        ) : (
          <>{linkContent}</>
        )}
      </Box>
    </>
  );
};
